import React, { createContext, useState, useEffect } from 'react';

const CartContext = createContext();

const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false); // Track if initial load is complete

    // Load cart from localStorage on initial render
    useEffect(() => {
        const loadCart = () => {
            try {
                const savedCart = localStorage.getItem('cart');
                if (savedCart) {
                    const parsedCart = JSON.parse(savedCart);
                    // Validate the parsed cart data
                    if (Array.isArray(parsedCart)) {
                        setCartItems(parsedCart);
                    }
                }
            } catch (error) {
                console.error('Failed to load cart from localStorage:', error);
                // Clear corrupted cart data
                localStorage.removeItem('cart');
            }
            setIsLoaded(true);
        };

        loadCart();
    }, []);

    // Save cart to localStorage whenever it changes
    useEffect(() => {
        if (isLoaded) { // Only save after initial load is complete
            try {
                localStorage.setItem('cart', JSON.stringify(cartItems));
            } catch (error) {
                console.error('Failed to save cart to localStorage:', error);
            }
        }
    }, [cartItems, isLoaded]);

    const removeFromCart = (productId, selectedImage) => {
        setCartItems(prevItems =>
            prevItems.filter(item =>
                !(item.id === productId && item.selectedImage === selectedImage)
            )
        );
    };

    const updateQuantity = (productId, selectedImage, newQuantity) => {
        setCartItems(prevItems =>
            prevItems.map(item =>
                item.id === productId && item.selectedImage === selectedImage
                    ? { ...item, quantity: newQuantity }
                    : item
            )
        );
    };

    const addToCart = (product, quantity = 1) => {
        setCartItems(prevItems => {
            const existingItem = prevItems.find(item =>
                item.id === product.id &&
                item.selectedSize === product.selectedSize &&
                item.selectedImage === product.selectedImage
            );

            if (existingItem) {
                return prevItems.map(item =>
                    item.id === product.id &&
                        item.selectedSize === product.selectedSize &&
                        item.selectedImage === product.selectedImage
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            }

            return [...prevItems, { ...product, quantity }];
        });
    };

    const updateSize = (productId, selectedImage, newSize) => {
        setCartItems(prevItems =>
            prevItems.map(item =>
                item.id === productId && item.selectedImage === selectedImage
                    ? { ...item, selectedSize: newSize }
                    : item
            )
        );
    };

    const clearCart = () => {
        setCartItems([]);
    };

    const getCartTotal = () => {
        return cartItems.reduce(
            (total, item) => total + (item.price * item.quantity),
            0
        );
    };

    const getCartCount = () => {
        return cartItems.reduce((count, item) => count + item.quantity, 0);
    };

    return (
        <CartContext.Provider
            value={{
                cartItems,
                addToCart,
                removeFromCart,
                updateQuantity,
                updateSize,
                clearCart,
                getCartTotal,
                getCartCount
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export { CartContext, CartProvider };